import type { Static } from 'runtypes';
import { Literal, Record as RtRecord, String, Union } from 'runtypes';
import type { OnlyOneOf } from 'bc-utils/types';
import type { BriefAccessRoleType } from './access';
import type { AccessRequestReadType } from './accessRequests';
import type { NotificationReadType } from './notifications';
import type { AsyncNotificationDeliveryReadType } from './asyncNotifications';
import type { OTArtifactType } from './ot';
import type { DynamicEnvClientConfigName } from './dynamicConfigs';
import type { LoginInfoMessageEnum } from './session';
import type { AgentflowEvent } from '../agentflow';

type LogoutEventPayload = { reason: LoginInfoMessageEnum };

type DuplicateEventPayload = { duplicateId: string } & (
  | { progress: 0; briefId?: never; error: any }
  | { progress: 1; briefId: number; error?: never }
);

type ExportEventPayload = { exportId: string } & (
  | { progress: 0; error: string }
  | { progress: 1; error?: never }
);

type ImportEventPayload = { importId: string } & (
  | { briefId?: never; progress: 0; error: any }
  | { briefId: number; progress: 1; error?: never }
);

type VersionEventPayload = string | undefined;

type BriefEventPayload = { briefId: number };

type ConfigUpdatedPayload = Partial<{ [configKey in DynamicEnvClientConfigName]: number }>;

type BackgroundJobRunPayload = {
  uuid: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'terminating' | 'terminated';
  progressMessage: string | null;
  triggeredByName: string;
  startedAt: string;
  completedAt: string | null;
  report: Record<string, unknown> | null;
  canTerminate: boolean;
};

type BackgroundJobUpdatedPayload = {
  jobId: string;
  run: BackgroundJobRunPayload;
};

// #region db events
// TODO replace the payload types with the actual table model interface for each table

type DbBriefEventTables =
  | OTArtifactType
  | 'attachments'
  | 'brief_access'
  | 'views'
  | 'snapshots'
  | 'notifications';

type DbBriefEvents = `${Exclude<
  DbBriefEventTables,
  'snapshots' | 'brief_access' | 'notifications'
>}:${'created' | 'updated' | 'deleted'}`;

type DbBriefEventPayload = { briefId: number; id: number };

type AccessRequestEvents = `access_requests:${'created' | 'updated' | 'deleted'}`;

type BriefAccessEvents = `brief_access:${'created' | 'updated' | 'deleted'}`;

type BriefAccessEventPayload = {
  id: number;
  briefId: number;
  notifiedBriefId: number;
  role: Exclude<BriefAccessRoleType, 'owner'>;
} & OnlyOneOf<
  {
    accountId: number;
    groupId: number;
    organizationId: number;
  },
  null
>;

type SnapshotEvents = `snapshots:${'created' | 'updated' | 'deleted'}`;

type SnapshotEventPayload = { artifactId: number; tableName: string };

type NotificationEvents = `notifications:${'created' | 'updated' | 'deleted'}`;

type AsyncNotificationDeliveryEvents = `async_notification_delivery:${'created' | 'updated' | 'deleted'}`;

type DbEventNonBriefTables =
  | 'access_requests'
  | 'subscriptions'
  | 'group_access'
  | 'integrations'
  | 'groups'
  | 'organization_access'
  | 'organizations'
  | 'feature_flags';
type PrintEvents = 'print';

type PrintEventsPayload = {
  type: 'pdf' | 'embed' | 'error' | 'checkQueue' | 'queueUpdate';
  socketId: string;
  briefId: string;
  orderId: string;
  data?: unknown;
  showLineNumbers?: boolean;
  originalUrl?: string;
  queuePos?: number;
};

type DbNonBriefEvents = `${Exclude<
  DbEventNonBriefTables,
  'organization_access' | 'groups' | 'group_access'
>}:${'created' | 'updated' | 'deleted'}`;

type DbNonBriefEventPayload = { id: number };

type GroupsEvents = `groups:${'created' | 'updated' | 'deleted'}`;

type GroupsEventPayload = { organizationId: number };

type OrganizationAccessEvents = `organization_access:${'created' | 'updated' | 'deleted'}`;

type OrganizationAccessEventPayload = { targetOrganizationId: number };

type GroupAccessEvents = `group_access:${'created' | 'updated' | 'deleted'}`;

type GroupAccessEventPayload = { targetOrganizationId: number; targetGroupId: number };
// #endregion db events

// #region clicksteam
// please keep this list alphabetized

enum ClickstreamEventEnum {
  ACCOUNT_CREATED = 'Account Created',
  AI_SHORTHAND_ABANDONED = 'AI Shorthand Abandoned',
  AI_SHORTHAND_ACCEPTED = 'AI Shorthand Accepted',
  AI_SHORTHAND_FAILED = 'AI Shorthand Failed',
  AI_SHORTHAND_RECEIVED = 'AI Shorthand Received',
  AI_SHORTHAND_REJECTED = 'AI Shorthand Rejected',
  AI_SHORTHAND_REQUESTED = 'AI Shorthand Requested',
  AI_ASSIST_ABANDONED = 'AI Assist Abandoned',
  AI_ASSIST_RECEIVED = 'AI Assist Received',
  AI_ASSIST_FAILED = 'AI Assist Failed',
  AI_ASSIST_REQUESTED = 'AI Assist Requested',
  AI_ASSIST_THUMBS_UP = 'AI Assist Thumbs Up',
  AI_ASSIST_THUMBS_DOWN = 'AI Assist Thumbs Down',
  AI_ASSIST_RETRY = 'AI Assist Retry',
  BRIEF_ACCESSED = 'Brief Accessed',
  EXCEPTION_UNHANDLED = 'Exception Unhandled',
  MENU_ITEM_CLICKED = 'Menu Item Clicked',
  NOTICE_DISMISSED = 'Notice Dismissed',
  NOTICE_MUTED = 'Notice Muted',
  NOTICE_SNOOZED = 'Notice Snoozed',
  PAGE_VIEWED = 'Page Viewed',
  SESSION_INITIATED = 'Session Initiated',
  SESSION_TERMINATED = 'Session Terminated',
}

const ClickstreamEventRuntype = Union(
  Literal(ClickstreamEventEnum.ACCOUNT_CREATED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_ABANDONED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_ACCEPTED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_FAILED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_RECEIVED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_REJECTED),
  Literal(ClickstreamEventEnum.AI_SHORTHAND_REQUESTED),
  Literal(ClickstreamEventEnum.AI_ASSIST_ABANDONED),
  Literal(ClickstreamEventEnum.AI_ASSIST_RECEIVED),
  Literal(ClickstreamEventEnum.AI_ASSIST_FAILED),
  Literal(ClickstreamEventEnum.AI_ASSIST_REQUESTED),
  Literal(ClickstreamEventEnum.AI_ASSIST_THUMBS_UP),
  Literal(ClickstreamEventEnum.AI_ASSIST_THUMBS_DOWN),
  Literal(ClickstreamEventEnum.AI_ASSIST_RETRY),
  Literal(ClickstreamEventEnum.BRIEF_ACCESSED),
  Literal(ClickstreamEventEnum.EXCEPTION_UNHANDLED),
  Literal(ClickstreamEventEnum.MENU_ITEM_CLICKED),
  Literal(ClickstreamEventEnum.NOTICE_DISMISSED),
  Literal(ClickstreamEventEnum.NOTICE_MUTED),
  Literal(ClickstreamEventEnum.NOTICE_SNOOZED),
  Literal(ClickstreamEventEnum.PAGE_VIEWED),
  Literal(ClickstreamEventEnum.SESSION_INITIATED),
  Literal(ClickstreamEventEnum.SESSION_TERMINATED)
);

const BaseClickstreamEventRuntype = RtRecord({
  sentAt: String,
  event: ClickstreamEventRuntype,
});

type BaseClickstreamEvent = Static<typeof BaseClickstreamEventRuntype>;

const ClickstreamLocaleContextRuntype = RtRecord({
  locale: String,
});

const ClickstreamPageContextRuntype = RtRecord({
  page: RtRecord({
    path: String,
    referrer: String,
    search: String,
    title: String,
    url: String,
  }),
});

const ClickstreamTimeZoneContextRuntype = RtRecord({
  timeZone: String,
});

const ClickstreamUserAgentContextRuntype = RtRecord({
  userAgent: String,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BrowserContextRuntype = ClickstreamLocaleContextRuntype.And(ClickstreamPageContextRuntype)
  .And(ClickstreamTimeZoneContextRuntype)
  .And(ClickstreamUserAgentContextRuntype);

type BrowserContext = Static<typeof BrowserContextRuntype>;

type BrowserEvent = BaseClickstreamEvent & {
  context: BrowserContext;
};

// #endregion clicksteam

type SessionEventDataMap = {
  'server:rebalance': undefined;
  logout: LogoutEventPayload;
  'duplicate-event': DuplicateEventPayload;
  'export-event': ExportEventPayload;
  'import-event': ImportEventPayload;
  version: VersionEventPayload;
  leave: BriefEventPayload;
  join: BriefEventPayload;
  'clickstream-event': BrowserEvent;
  'dynamic_configs:updated': ConfigUpdatedPayload;
  'admin:background-jobs:join': undefined;
  'admin:background-jobs:leave': undefined;
  'background_job:updated': BackgroundJobUpdatedPayload;
  'agentflow:event': AgentflowEvent;
} & {
  [K in DbBriefEvents]: DbBriefEventPayload;
} & {
  [K in DbNonBriefEvents]: DbNonBriefEventPayload;
} & {
  [K in SnapshotEvents]: SnapshotEventPayload;
} & {
  [K in AccessRequestEvents]: AccessRequestReadType;
} & {
  [K in OrganizationAccessEvents]: OrganizationAccessEventPayload;
} & {
  [K in GroupsEvents]: GroupsEventPayload;
} & {
  [K in GroupAccessEvents]: GroupAccessEventPayload;
} & {
  [K in BriefAccessEvents]: BriefAccessEventPayload;
} & {
  [K in PrintEvents]: PrintEventsPayload;
} & {
  [K in NotificationEvents]: NotificationReadType;
} & {
  [K in AsyncNotificationDeliveryEvents]: AsyncNotificationDeliveryReadType;
};

export {
  BaseClickstreamEventRuntype,
  ClickstreamEventRuntype,
  ClickstreamEventEnum,
  ClickstreamLocaleContextRuntype,
  ClickstreamPageContextRuntype,
  ClickstreamTimeZoneContextRuntype,
  ClickstreamUserAgentContextRuntype,
};
export type {
  BackgroundJobUpdatedPayload,
  DuplicateEventPayload,
  ExportEventPayload,
  GroupAccessEventPayload,
  ImportEventPayload,
  LogoutEventPayload,
  VersionEventPayload,
  BriefEventPayload,
  SessionEventDataMap,
  BaseClickstreamEvent,
  AsyncNotificationDeliveryEvents
};
